import { c as _c } from "react/compiler-runtime";
import type { StaticRoute, DynamicRoute } from 'src/types/routes';
import { useCallback, useMemo } from 'react';
import { ROUTES } from 'src/constants/routes';
import { useRouteNames } from './useRouteNames';
type RoutePattern = StaticRoute | DynamicRoute;
type RouteParams = Record<string, string>;
interface Match {
  matched: true;
  key: string;
  pattern: RoutePattern;
  params: RouteParams;
  title: string;
}
interface NoMatch {
  matched: false;
  title: string;
  key?: null;
}
const DEFAULT_TITLE = 'Effektify One';
interface RouteDefinitions {
  key: string;
  pattern: RoutePattern;
  re: RegExp;
  paramNames: string[];
  title?: string;
}
;
const getPatternParamNames = (pattern: RoutePattern): string[] => {
  const paramNames: string[] = [];
  pattern.split('/').forEach(segment => {
    if (segment.startsWith(':')) {
      paramNames.push(segment.slice(1));
    }
  });
  return paramNames;
};
const routeRegExpMatcher = (pattern: RoutePattern): RegExp => {
  const escaped = pattern.split('/').map(segment => {
    if (segment.startsWith(':')) {
      return '([^/]+)';
    }
    return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }).join('/');
  return new RegExp(`^${escaped}$`);
};
const compile = (pattern: RoutePattern, key: string, title: string): RouteDefinitions => {
  const paramNames: string[] = getPatternParamNames(pattern);
  const re = routeRegExpMatcher(pattern);
  return {
    key,
    pattern,
    re,
    paramNames,
    title
  };
};
const isMatch = (obj: Match | NoMatch): obj is Match => obj.matched === true;
const isNoMatch = (obj: Match | NoMatch): obj is NoMatch => obj.matched === false;
const useRouteDefs = () => {
  const $ = _c(11);
  const ROUTE_NAMES = useRouteNames();
  let t0;
  if ($[0] !== ROUTE_NAMES) {
    t0 = Object.entries(ROUTES).map(t1 => {
      const [key, pattern] = t1;
      return compile(pattern as RoutePattern, key, ROUTE_NAMES[pattern as keyof typeof ROUTE_NAMES]);
    });
    $[0] = ROUTE_NAMES;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const routeDefinitions = t0;
  let t1;
  if ($[2] !== routeDefinitions) {
    t1 = pathname => {
      if (!pathname) {
        return {
          matched: false,
          title: DEFAULT_TITLE
        };
      }
      for (const route of routeDefinitions) {
        const m = pathname.match(route.re);
        if (m) {
          const params = route.paramNames.reduce((acc, name, i) => {
            acc[name] = m[i + 1] ?? "";
            return acc;
          }, {});
          const title = route.title || route.key;
          return {
            key: route.key,
            matched: true,
            pattern: route.pattern,
            params,
            title
          };
        }
      }
      return {
        matched: false,
        title: DEFAULT_TITLE
      };
    };
    $[2] = routeDefinitions;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const matchPathname = t1;
  let t2;
  if ($[4] !== matchPathname) {
    t2 = pathname_0 => matchPathname(pathname_0);
    $[4] = matchPathname;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const getCurrentRouteDef = t2;
  let t3;
  if ($[6] !== matchPathname) {
    t3 = pathname_1 => {
      const match = matchPathname(pathname_1);
      return match.matched ? match.params : {};
    };
    $[6] = matchPathname;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getRouteParams = t3;
  const getPatternParams = _temp;
  let t4;
  if ($[8] !== getCurrentRouteDef || $[9] !== getRouteParams) {
    t4 = {
      getCurrentRouteDef,
      getRouteParams,
      getPatternParams
    };
    $[8] = getCurrentRouteDef;
    $[9] = getRouteParams;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export default useRouteDefs;
export { isMatch, isNoMatch };
export type { Match, NoMatch, RouteParams, RoutePattern };

// Maps each route key to its ordered ancestor route keys (root → parent)
export const ROUTE_ANCESTORS: Record<string, string[]> = {
  CAMPAIGN_DETAIL: ['CAMPAIGNS'],
  ORGANIC_POSTS: ['CAMPAIGNS'],
  AD_STUDIO: ['CAMPAIGNS', 'CAMPAIGN_DETAIL'],
  CAMPAIGN_PUBLISH: ['CAMPAIGNS', 'CAMPAIGN_DETAIL'],
  DASHBOARD_DETAIL: ['DASHBOARDS']
};
function _temp(pattern_0) {
  return getPatternParamNames(pattern_0);
}